/**
 * Reel clips in the browser: read the facts the editor shows (size, duration) and take the
 * poster frame as WebP. No transcoding here or on the server; the server re-measures the file.
 */
export function readVideo(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    const cleanup = () => { video.removeAttribute('src'); video.load() }
    video.addEventListener('loadedmetadata', () => {
      resolve({ video, width: video.videoWidth, height: video.videoHeight, duration: video.duration, cleanup })
    }, { once: true })
    video.addEventListener('error', () => { cleanup(); reject(new Error('unreadable')) }, { once: true })
    video.src = url
  })
}

function seek(video, time) {
  return new Promise((resolve, reject) => {
    const done = () => { video.removeEventListener('error', fail); resolve() }
    const fail = () => { video.removeEventListener('seeked', done); reject(new Error('seek')) }
    video.addEventListener('seeked', done, { once: true })
    video.addEventListener('error', fail, { once: true })
    video.currentTime = time
  })
}

/** The first frame (a tenth of a second in, past black lead-in frames), fitted in the box. */
export async function extractPoster(url, { maxWidth = 600, maxHeight = 1200, quality = 0.82 } = {}) {
  const { video, width, height, duration, cleanup } = await readVideo(url)
  try {
    if (!width || !height) throw new Error('no frame')
    await seek(video, Math.min(0.1, (duration || 0) / 2))
    const scale = Math.min(1, maxWidth / width, maxHeight / height)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
    if (!blob) throw new Error('no poster')
    // A browser without a WebP encoder hands back PNG, which the poster profile also accepts.
    const extension = blob.type === 'image/webp' ? 'webp' : 'png'
    return { file: new File([blob], `poster.${extension}`, { type: blob.type }), width, height, duration }
  } finally {
    cleanup()
  }
}

export function formatDuration(seconds) {
  const whole = Math.round(Number(seconds) || 0)
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export function formatBytes(bytes) {
  return `${(Number(bytes || 0) / 1048576).toFixed(1)} MB`
}
